import JSZip from "jszip";

/**
 * Downloads a base64 file as ZIP, extracting .dat content if necessary
 * @param base64Content File content in base64 format
 * @param fileName Name of the file to download
 */
export async function downloadBase64AsZip(base64Content: string, fileName: string): Promise<void> {
  try {
    // Convert base64 content to blob
    const binaryContent = atob(base64Content);
    const bytes = new Uint8Array(binaryContent.length);

    for (let i = 0; i < binaryContent.length; i++) {
      bytes[i] = binaryContent.charCodeAt(i);
    }

    const blob = new Blob([bytes], { type: "application/zip" });

    // Load ZIP with JSZip to check if it contains a .dat file
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(blob);
    const files = zipContent.files;
    const fileNames = Object.keys(files);

    // If there's a single .dat file, extract and download it as ZIP
    if (fileNames.length === 1 && fileNames[0].endsWith(".dat")) {
      const datFile = files[fileNames[0]];
      const datBlob = await datFile.async("blob");

      // Download .dat as ZIP
      downloadBlob(datBlob, fileName.endsWith(".zip") ? fileName : `${fileName}.zip`);
    } else {
      // If there's no .dat file or multiple files, download the original ZIP
      downloadBlob(blob, fileName.endsWith(".zip") ? fileName : `${fileName}.zip`);
    }
  } catch (error) {
    alert("Error downloading file " + fileName + ". Please try again.");
  }
}

/**
 * Downloads multiple base64 files as ZIPs
 * @param files Array of files with base64 content and name
 * @param delay Delay between downloads in ms (to avoid browser blocking)
 */
export async function downloadMultipleFiles(
  files: Array<{ fileContent: string; apiName: string }>,
  delay = 1000
): Promise<void> {
  try {
    if (files.length === 0) {
      return;
    }

    // Create a ZIP containing all individual files
    const mainZip = new JSZip();

    // For each file in the array
    for (const file of files) {
      if (!file.fileContent) {
        continue;
      }

      try {
        // Convert base64 content to blob
        const binaryContent = atob(file.fileContent);
        const bytes = new Uint8Array(binaryContent.length);

        for (let i = 0; i < binaryContent.length; i++) {
          bytes[i] = binaryContent.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: "application/zip" });

        // Load ZIP with JSZip to check if it contains a .dat file
        const zip = new JSZip();
        const zipContent = await zip.loadAsync(blob);
        const zipFiles = zipContent.files;
        const fileNames = Object.keys(zipFiles);

        // If there's a single .dat file, extract and add it to the main ZIP
        if (fileNames.length === 1 && fileNames[0].endsWith(".dat")) {
          const datFile = zipFiles[fileNames[0]];
          const datContent = await datFile.async("arraybuffer");

          // Add .dat content to the main ZIP as a .zip file
          mainZip.file(`${file.apiName}.zip`, datContent);
        } else {
          // If there's no .dat file or multiple files, add the original ZIP
          mainZip.file(`${file.apiName}.zip`, blob);
        }
      } catch (error) {
        // Continue with other files if one fails
      }
    }

    // Generate the final ZIP with all files
    const finalZipBlob = await mainZip.generateAsync({ type: "blob" });

    // Download the final ZIP
    downloadBlob(finalZipBlob, "all_migrated_apis.zip");
  } catch (error) {
    alert("Error downloading files. Please try again.");
  }
}

/**
 * Downloads content based on format
 * @param base64Content File content in base64
 * @param fileName File name with extension
 */
export async function downloadContent(base64Content: string, fileName: string): Promise<void> {
  try {
    const extension: string = fileName.split(".").pop() || "";
    const decodedContent: string = atob(base64Content.replace(/-/g, "+").replace(/_/g, "/"));

    let mimeType = "application/octet-stream";
    if (extension === "yaml" || extension === "yml") {
      mimeType = "application/yaml";
    }
    const blob = new Blob([decodedContent], { type: mimeType });
    downloadBlob(blob, fileName);
  } catch (error) {
    console.error("Error saving file:", error);
  }
}

/**
 * Downloads content as YAML
 * @param content YAML content
 * @param fileName File name
 */
export async function downloadAsYaml(content: string, fileName: string): Promise<void> {
  try {
    console.log("Starting downloadAsYaml");

    // Verify content is valid
    if (!content || content.trim() === "") {
      throw new Error("Content is empty");
    }

    // Create blob with content
    const blob = new Blob([content], { type: "application/yaml" });
    console.log("Blob created:", blob.size, "bytes");

    // Download the blob
    downloadBlob(blob, fileName);
    console.log("Download completed");
  } catch (error) {
    console.error("Error in downloadAsYaml:", error);
    throw error;
  }
}

/**
 * Helper function to download a blob
 * @param blob The blob to download
 * @param fileName The name to save the file as
 */
function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  // Small delay before cleanup
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
